import type {ConnectionOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {type QueryKey, useQueryClient} from '@github-ui/react-query'
import {deleteIssueProjectsMutation as deleteIssueProjectsQueryId} from '@github-ui/server-persisted-queries'
import type {DeleteIssueProjectsMutationMutation} from '@github-ui/server-persisted-queries/types/deleteIssueProjectsMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, ConnectionHandler, type Disposable, type RecordSourceProxy} from 'relay-runtime'

export const PROJECT_ITEMS_CONNECTION_PATH = ['projectItems']

type RemoveProjectOperation = Extract<ConnectionOperation, {type: 'removeFromConnection'}>

type DeleteIssueProjectsCallbacks = {
  onError?: (error: Error) => void
  onCompleted?: (response: DeleteIssueProjectsMutationMutation) => void
}

type DeleteIssueProjects = (itemId: string, callbacks?: DeleteIssueProjectsCallbacks) => void

function removeProjectOperation(itemId: string): RemoveProjectOperation {
  return {type: 'removeFromConnection', connectionPath: PROJECT_ITEMS_CONNECTION_PATH, nodeId: itemId}
}

function removeProjectItem(store: RecordSourceProxy, connectionId: string, itemId: string): void {
  const connection = store.get(connectionId)
  if (connection) ConnectionHandler.deleteNode(connection, itemId)
}

async function deleteIssueProjectsRequest({
  owner,
  repo,
  issueNumber,
  itemId,
}: {
  owner: string
  repo: string
  issueNumber: number
  itemId: string
}): Promise<DeleteIssueProjectsMutationMutation> {
  const query = new URLSearchParams({queryId: deleteIssueProjectsQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/project_item.json?${query}`,
    {method: 'DELETE', body: {itemId}},
  )

  let result:
    | {data?: DeleteIssueProjectsMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null}
    | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  if (!response.ok || error || data?.deleteProjectV2Item?.deletedItemId !== itemId) {
    throw new Error(error ?? 'Failed to remove issue from project')
  }
  return data
}

async function notifyMutationResult(
  mutation: Promise<DeleteIssueProjectsMutationMutation>,
  callbacks?: DeleteIssueProjectsCallbacks,
): Promise<void> {
  let response: DeleteIssueProjectsMutationMutation
  try {
    response = await mutation
  } catch (error) {
    callbacks?.onError?.(error instanceof Error ? error : new Error('Failed to remove issue from project'))
    return
  }
  callbacks?.onCompleted?.(response)
}

export function useDeleteIssueProjectsMutation({
  owner,
  repo,
  issueNumber,
  issueId,
  connectionId,
  settlementQueryKey,
}: {
  owner: string
  repo: string
  issueNumber: number
  issueId: string
  connectionId: string
  settlementQueryKey?: QueryKey
}): DeleteIssueProjects {
  const environment = useRelayEnvironment()
  const queryClient = useQueryClient()
  const {mutateAsync} = useConnectionMutation<DeleteIssueProjectsMutationMutation, Error, Disposable>({
    ownerId: issueId,
    connectionPath: PROJECT_ITEMS_CONNECTION_PATH,
    mutationFn: operation =>
      deleteIssueProjectsRequest({owner, repo, issueNumber, itemId: (operation as RemoveProjectOperation).nodeId}),
    // RELAY-CLEANUP(ProjectsSectionQuery): mirror removals until the projects section reads TanStack data.
    onMutate: ({operation}) =>
      environment.applyUpdate({
        storeUpdater: store => removeProjectItem(store, connectionId, (operation as RemoveProjectOperation).nodeId),
      }),
    onSuccess: async (data, _variables, revertRelayStore) => {
      const deletedItemId = data.deleteProjectV2Item?.deletedItemId
      try {
        if (deletedItemId) {
          const operation = removeProjectOperation(deletedItemId)
          if (settlementQueryKey) {
            await settleConfirmedOperation({
              queryClient,
              targets: [{queryKey: settlementQueryKey}],
              ownerId: issueId,
              operation,
            })
          }
          await notifySettlement(operation, issueId, PROJECT_ITEMS_CONNECTION_PATH)
          commitLocalUpdate(environment, store => removeProjectItem(store, connectionId, deletedItemId))
        }
      } finally {
        revertRelayStore?.dispose()
      }
    },
    onError: (_error, _variables, revertRelayStore) => revertRelayStore?.dispose(),
  })

  return useCallback(
    (itemId, callbacks) => {
      void notifyMutationResult(mutateAsync({operation: removeProjectOperation(itemId)}), callbacks)
    },
    [mutateAsync],
  )
}
